import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Zap, Shield, Star, ArrowRight, Loader2, CreditCard } from "lucide-react";
import { API, useAuth } from "../App";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    period: "",
    description: "Upload, extract, and review all your transactions. No card required.",
    badge: null,
    color: "gray",
    features: [
      "1 statement per financial year",
      "Automatic ATO categorisation",
      "All 12 ATO deduction categories",
      "In-app review and editing",
      "Duplicate detection",
    ],
    limitations: [
      "No Excel export",
      "No Quick Sort mode",
      "No multi-statement merge",
    ],
    cta: "Current Plan",
    disabled: true,
    cancelNote: null,
  },
  {
    id: "pro_monthly",
    name: "Pro Monthly",
    price: 9,
    period: "/month",
    description: "Everything in Free, plus the accountant-ready Excel workbook.",
    badge: "Most Popular",
    color: "primary",
    features: [
      "Unlimited statements per year",
      "Excel workbook export (7-sheet)",
      "ATO deduction codes included",
      "Quick Sort bulk categorisation",
      "Multi-statement merge & deduplicate",
      "Accountant-ready formatting",
      "Priority support",
    ],
    limitations: [],
    cta: "Start Pro — $9/month",
    disabled: false,
    cancelNote: "Cancel anytime — Pro access continues to end of billing period.",
  },
  {
    id: "pro_annual",
    name: "Pro Annual",
    price: 69,
    period: "/year",
    annualNote: "Save $39 vs monthly",
    description: "Best value. $5.75/month billed annually.",
    badge: "Best Value",
    color: "green",
    features: [
      "Everything in Pro Monthly",
      "14-day automatic refund",
      "Early access to new features",
      "Accountant sharing link (coming soon)",
    ],
    limitations: [],
    cta: "Get Pro Annual — $69/year",
    disabled: false,
    cancelNote: "14-day automatic refund. After 14 days, access continues to year end.",
  },
];

function PlanCard({ plan, currentPlan, onSelect, loading }) {
  const isCurrentPlan = currentPlan === plan.id;
  const isFeatured = plan.badge === "Most Popular";

  return (
    <div className={`relative bg-white border-2 rounded-2xl p-6 flex flex-col transition-all ${
      isFeatured
        ? "border-blue-600 shadow-xl scale-[1.02]"
        : "border-gray-200 hover:border-gray-300 shadow-sm"
    }`}>
      {plan.badge && (
        <div className={`absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black whitespace-nowrap ${
          plan.badge === "Most Popular"
            ? "bg-blue-600 text-white"
            : "bg-green-600 text-white"
        }`}>
          {plan.badge}
        </div>
      )}

      <div className="mb-5">
        <h3 className="font-bold text-gray-900 text-lg">{plan.name}</h3>
        <div className="flex items-end gap-1 mt-1">
          <span className="text-4xl font-black text-gray-900">${plan.price}</span>
          <span className="text-gray-500 text-sm mb-1.5">{plan.period}</span>
        </div>
        {plan.annualNote && (
          <span className="inline-block mt-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-100">
            {plan.annualNote}
          </span>
        )}
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">{plan.description}</p>
      </div>

      <ul className="flex-1 space-y-2 mb-5">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
            <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
            {f}
          </li>
        ))}
        {plan.limitations.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-gray-400">
            <span className="text-gray-300 flex-shrink-0 mt-0.5 font-bold text-xs">✕</span>
            {f}
          </li>
        ))}
      </ul>

      <button
        onClick={() => !plan.disabled && !isCurrentPlan && !loading && onSelect(plan)}
        disabled={plan.disabled || isCurrentPlan || loading}
        className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition ${
          isCurrentPlan
            ? "bg-gray-100 text-gray-500 cursor-default"
            : plan.disabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : loading
            ? "opacity-70 cursor-wait"
            : isFeatured
            ? "text-white hover:opacity-90"
            : "bg-gray-900 text-white hover:bg-gray-800"
        }`}
        style={isFeatured && !isCurrentPlan ? { background: "linear-gradient(135deg, #d97706, #b45309)" } : {}}
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        {isCurrentPlan ? "Your current plan" : loading ? "Redirecting…" : plan.cta}
        {!isCurrentPlan && !plan.disabled && !loading && <ArrowRight size={14} />}
      </button>

      {plan.cancelNote && !isCurrentPlan && (
        <p className="text-[10px] text-gray-400 text-center mt-2 leading-snug">{plan.cancelNote}</p>
      )}
    </div>
  );
}

// Maps plan IDs to the "plan" key the backend expects
const PLAN_KEY = { pro_monthly: "monthly", pro_annual: "annual" };

export default function Pricing() {
  const { user } = useAuth();
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const currentPlan = user?.subscription?.plan || "free";

  const handleSelectPlan = async (plan) => {
    const planKey = PLAN_KEY[plan.id];
    if (!planKey) return;
    setLoadingPlanId(plan.id);
    try {
      const res = await axios.post(`${API}/api/stripe/create-checkout-session`, {
        plan: planKey,
        success_url: `${window.location.origin}/settings?upgraded=true`,
        cancel_url: `${window.location.origin}/pricing`,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Could not start checkout. Please try again or contact findjoy143@gmail.com"
      );
      setLoadingPlanId(null);
    }
    // Don't clear loadingPlanId on success — page is redirecting to Stripe
  };

  const handleManageBilling = async () => {
    setLoadingPlanId("portal");
    try {
      const res = await axios.post(`${API}/api/billing/portal`);
      window.location.href = res.data.portal_url;
    } catch {
      toast.error("Could not open billing portal. Contact findjoy143@gmail.com");
      setLoadingPlanId(null);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-black text-gray-900 mb-3 tracking-tight">Clear, honest pricing</h1>
        <p className="text-gray-500 max-w-xl mx-auto leading-relaxed">
          The free plan lets you upload and review everything. Pro unlocks the Excel export your accountant needs.
          No surprises.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
        {PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            currentPlan={currentPlan}
            onSelect={handleSelectPlan}
            loading={loadingPlanId === plan.id}
          />
        ))}
      </div>

      {/* Manage billing for Pro users */}
      {currentPlan !== "free" && (
        <div className="max-w-4xl mx-auto mb-10">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard size={18} className="text-gray-500" />
              <div>
                <p className="font-semibold text-sm text-gray-800">Manage your subscription</p>
                <p className="text-xs text-gray-500">Update payment method, view invoices, or cancel</p>
              </div>
            </div>
            <button
              onClick={handleManageBilling}
              disabled={loadingPlanId !== null}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-white transition flex items-center gap-2"
            >
              {loadingPlanId === "portal" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              Billing Portal
            </button>
          </div>
        </div>
      )}

      {/* Trust signals */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {[
          { icon: Shield, title: "Secure payments", desc: "Processed by Stripe — we never see your card details" },
          { icon: Star, title: "14-day automatic refund", desc: "Annual plan only — automatic, no questions asked, within 14 days of purchase" },
          { icon: Zap, title: "Cancel anytime", desc: "Monthly: access to end of billing period. Annual: access to end of year." },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="text-center p-5 bg-gray-50 rounded-xl border border-gray-100">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Icon size={18} className="text-blue-600" />
            </div>
            <p className="font-bold text-sm text-gray-900 mb-1">{title}</p>
            <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>

      {/* ACL note */}
      <div className="max-w-4xl mx-auto">
        <p className="text-center text-xs text-gray-400 leading-relaxed">
          Prices in AUD. Australian Consumer Law guarantees apply to all purchases regardless of the above.
          Questions? <a href="mailto:findjoy143@gmail.com" className="underline hover:text-gray-600">findjoy143@gmail.com</a>
        </p>
      </div>

      {/* FAQ */}
      <div className="max-w-2xl mx-auto mt-10">
        <h2 className="font-black text-gray-900 text-xl mb-5 text-center tracking-tight">Common questions</h2>
        <div className="space-y-3">
          {[
            { q: "Do I need a card for the free plan?", a: "No. The free plan requires no credit card. Sign up with your email and start uploading immediately." },
            { q: "What exactly does the free plan include?", a: "The free plan lets you upload 1 statement per financial year, extract all transactions, and review them in the app — including all ATO category assignments. You can manually edit any category. Pro unlocks the Excel export and unlimited statements." },
            { q: "What is the 14-day refund exactly?", a: "Annual plans (Pro Annual at $69/year) are eligible for a full automatic refund if you request one within 14 days of purchase. After 14 days, your Pro access continues to the end of the annual period. Monthly plans are not eligible for refunds — you cancel and access continues to the end of the billing period." },
            { q: "Is TaxBuddy a registered tax agent?", a: "No. TaxBuddy is a record-keeping and organisation tool only. It categorises your transactions and helps you prepare records — lodging your return is still done through myTax or a registered tax agent." },
            { q: "What if my accountant prefers a different format?", a: "TaxBuddy exports in two styles — a detailed professional format and a simple monthly layout. You can choose whichever your accountant prefers, or send both." },
          ].map(({ q, a }) => (
            <div key={q} className="bg-white border border-gray-100 rounded-xl p-5">
              <p className="font-bold text-sm text-gray-900 mb-2">{q}</p>
              <p className="text-sm text-gray-600 leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
